<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- Sidebar -->
      <aside class="sidebar">
        <div class="sb-top">
          <img :src="darkLogoUrl" class="sb-logo" />
          <div class="sb-rule"></div>

          <!-- LO identity -->
          <div class="sb-lo">
            <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="sb-avatar" />
            <div class="sb-tag">Loan Officer</div>
            <div class="sb-name">{{ loName }}</div>
            <div class="sb-role">{{ loTitle }}</div>
            <div v-if="loinfo && loinfo.nmls" class="sb-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
          </div>

          <div class="sb-rule"></div>

          <!-- Contact items -->
          <div class="sb-contacts">
            <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="sb-contact">
              <span class="sb-contact-icon">☎</span>
              <span>{{ loinfo.phone_number }}</span>
            </a>
            <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="sb-contact">
              <span class="sb-contact-icon">✉</span>
              <span>{{ loinfo.email }}</span>
            </a>
          </div>

          <div class="sb-rule"></div>
         
        </div>

        <!-- Sidebar bottom accent -->
        <div class="sb-bottom">
          <div class="sb-accent-bar"></div>
        </div>
      </aside>

      <!-- Main content -->
      <main class="main">

        <!-- Top bar -->
        <div class="main-topbar">
          <div class="mt-partnership">
            <span class="mt-lo">{{ loName || "Loan Officer" }}</span>
            <span class="mt-x">×</span>
            <span class="mt-re">{{ realtorName || "Partner" }}</span>
          </div>
  
        </div>

        <!-- Hero area -->
        <section class="main-hero">
       
          <h1 class="mh-h1">{{ heroH1Html }}</h1>
          <p class="mh-body">{{ heroBody }}</p>
          <div class="mh-btns">
            <a class="btn-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="btn-outline" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
        </section>

        <!-- Realtor card in main area -->
        <section class="re-section">
          <div class="re-card">
            <div class="re-card-left">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="re-avatar" />
            </div>
            <div class="re-card-body">
              <span class="re-tag">Partner</span>
              <div class="re-name">{{ realtorName }}</div>
              <div class="re-brokerage">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
              <div class="re-contacts">
                <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="re-chip">{{ realtor.phone_number }}</a>
                <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="re-chip">{{ realtor.email }}</a>
                <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="re-chip" target="_blank" rel="noreferrer">Website →</a>
              </div>
            </div>
          </div>
        </section>

      </main>
    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};
const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") { return `${DBA_BASE}/${brandKey}/${variant}.png`; }
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandDashboard",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    darkLogoUrl() { return dbaLogo(this.brandKey, "primary"); },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Geist:wght@300;400;500;600&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,400;0,600;1,400&family=Nunito+Sans:wght@300;400;500;600&display=swap');

.page {
  min-height: 100vh;
  display: flex;
  background: #f2f4f7;
  color: #111827;
  font-family: 'Nunito Sans', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --sidebar-w: 280px;
  --hairline: rgba(17,24,39,0.09);
  --muted: #64748b;
  --a-tint: color-mix(in srgb, var(--a) 12%, #fff);
  --p-tint: color-mix(in srgb, var(--p) 6%, #fff);
}
.root { display: flex; width: 100%; min-height: 100vh; }

/* SIDEBAR */
.sidebar {
  width: var(--sidebar-w);
  flex-shrink: 0;
  background: var(--p);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: sticky; top: 0; height: 100vh;
  overflow-y: auto;
}
.sb-top { padding: 28px 24px; display: flex; flex-direction: column; gap: 0; }
.sb-logo {  object-fit: contain; margin-bottom: 24px;  width: 160px; max-height:80px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.9;}
.sb-rule { height: 1px; background: rgba(255,255,255,0.10); margin: 18px 0; }
.sb-lo { display: flex; flex-direction: column; }
.sb-avatar { width: 64px; height: 64px; border-radius: 14px; object-fit: cover; margin-bottom: 12px; border: 2px solid rgba(255,255,255,0.12); }
.sb-tag { font-size: 9px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; color: var(--a); margin-bottom: 6px; }
.sb-name { font-family: 'Spectral', serif; font-size: 20px; font-weight: 600; color: #fff; line-height: 1.2; margin-bottom: 3px; }
.sb-role { font-size: 12px; color: rgba(255,255,255,0.5); margin-bottom: 2px; }
.sb-nmls { font-size: 11px; color: rgba(255,255,255,0.3); }
.sb-contacts { display: flex; flex-direction: column; gap: 10px; }
.sb-contact {
  display: flex; align-items: flex-start; gap: 10px;
  text-decoration: none; color: rgba(255,255,255,0.55);
  font-size: 12px; line-height: 1.4;
  transition: color 0.18s;
}
.sb-contact:hover { color: rgba(255,255,255,0.9); }
.sb-contact-icon { color: var(--a); font-size: 12px; margin-top: 1px; flex-shrink: 0; }
.sb-cta {
  display: block; text-align: center; text-decoration: none;
  background: var(--a); color: var(--p);
  font-size: 13px; font-weight: 600; padding: 12px;
  border-radius: 8px; transition: opacity 0.18s;
}
.sb-cta:hover { opacity: 0.88; }
.sb-bottom { padding: 0; }
.sb-accent-bar { height: 4px; background: linear-gradient(90deg, var(--a), color-mix(in srgb, var(--a) 40%, var(--p))); }

/* MAIN */
.main { flex: 1; display: flex; flex-direction: column; overflow-y: auto; }

.main-topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 40px;
  background: #fff;
  border-bottom: 1px solid var(--hairline);
  position: sticky; top: 0; z-index: 10;
}
.mt-partnership { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.mt-lo { font-weight: 600; color: var(--p); }
.mt-x { color: var(--a); }
.mt-re { color: var(--muted); }
.mt-apply {
  font-size: 13px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #fff;
  padding: 9px 20px; border-radius: 7px; transition: opacity 0.2s;
}
.mt-apply:hover { opacity: 0.85; }

/* HERO */
.main-hero { padding: 56px 48px 40px; background: #fff; border-bottom: 1px solid var(--hairline); }
.mh-kicker { font-size: 11px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase; color: var(--a); margin-bottom: 12px; }
.mh-h1 {
  font-family: 'Spectral', serif;
  font-size: clamp(36px, 4vw, 58px);
  font-weight: 600; line-height: 1.1;
  color: var(--p); margin: 0 0 16px;
}
.mh-accent { font-style: italic; color: color-mix(in srgb, var(--a) 85%, var(--p)); font-weight: 400; }
.mh-body { font-size: 15px; line-height: 1.75; color: var(--muted);  margin-bottom: 28px; }
.mh-btns { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;}
.btn-primary {
  font-size: 14px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #fff;
  padding: 13px 26px; border-radius: 8px; transition: opacity 0.2s;
  width:55%
}
.btn-primary:hover { opacity: 0.85; }
.btn-outline {
  font-size: 14px; font-weight: 500; text-decoration: none;
  color: var(--p); border: 1.5px solid var(--hairline);
  padding: 13px 26px; border-radius: 8px; transition: all 0.18s;
}
.btn-outline:hover { border-color: color-mix(in srgb, var(--p) 22%, transparent); background: var(--p-tint); }

/* REALTOR SECTION */
.re-section { padding: 32px 48px 48px; }
.re-card {
  background: #fff; border-radius: 16px;
  border: 1px solid var(--hairline);
  padding: 24px; display: flex; gap: 24px; align-items: flex-start;
  box-shadow: 0 2px 16px rgba(0,0,0,0.05);
  max-width: 680px;
}
.re-avatar { width: 72px; height: 72px; border-radius: 12px; object-fit: cover; flex-shrink: 0; }
.re-tag { font-size: 9px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: color-mix(in srgb, var(--a) 80%, var(--p)); display: block; margin-bottom: 6px; }
.re-name { font-family: 'Spectral', serif; font-size: 22px; font-weight: 600; color: var(--p); margin-bottom: 3px; }
.re-brokerage { font-size: 13px; color: var(--muted); margin-bottom: 14px; }
.re-contacts { display: flex; flex-wrap: wrap; gap: 6px; }
.re-chip {
  font-size: 11px; font-weight: 500; text-decoration: none;
  background: var(--a-tint); color: color-mix(in srgb, var(--a) 85%, var(--p));
  padding: 5px 12px; border-radius: 50px;
  border: 1px solid color-mix(in srgb, var(--a) 18%, transparent);
  transition: background 0.18s;
}
.re-chip:hover { background: color-mix(in srgb, var(--a) 20%, #fff); }

@media (max-width: 820px) {
  .root { flex-direction: column; }
  .sidebar { width: 100%; height: auto; position: static; }
  .sb-lo { flex-direction: row; align-items: center; gap: 14px; }
  .sb-avatar { margin-bottom: 0; }
  .main-hero { padding: 36px 24px 28px; }
  .re-section { padding: 24px; }
  .main-topbar { padding: 14px 24px; }
}
</style>
